import { PackageSearch } from 'lucide-react';
import { ProductCard } from '@/components/product/ProductCard';
import { EmptyState } from '@/components/system/EmptyState';
// One definition of what loading looks like, shared across the app.
import { ProductGridSkeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/system/ErrorState';
import { Button } from '@/components/ui/Button';
import type { ProductCard as ProductCardType } from '@/types/catalogue';
import { PRODUCT_GRID_NARROW_CLASS } from './grid';

/**
 * Product grid with its loading, empty and error states — BUILD-PLAN §9,
 * §41, §79.
 *
 * All four states live here rather than in each calling page, so no screen
 * can accidentally ship without one — the §79 "definition of done" for a
 * data-backed view.
 */

interface ProductGridProps {
  products: ProductCardType[];
  isLoading: boolean;
  isError: boolean;
  onRetry?: () => void;
  /** True when filters are applied — changes the empty-state copy. */
  isFiltered?: boolean;
  onClearFilters?: () => void;
}

export function ProductGrid({
  products,
  isLoading,
  isError,
  onRetry,
  isFiltered = false,
  onClearFilters,
}: ProductGridProps) {
  if (isError) {
    return (
      <ErrorState
        title="We couldn’t load the collection"
        description="Something went wrong fetching these products. Please try again."
        onRetry={onRetry}
      />
    );
  }

  if (isLoading) {
    return <ProductGridSkeleton />;
  }

  if (products.length === 0) {
    /*
     | Two distinct empty states. "No results for your filters" is a
     | recoverable dead end and needs a way out; "nothing here yet" is not
     | the customer's doing and must not imply they did something wrong.
     */
    return isFiltered ? (
      <EmptyState
        icon={PackageSearch}
        title="No pieces match those filters"
        description="Try widening your search — or explore the full collection."
        action={
          onClearFilters && (
            <Button variant="secondary" onClick={onClearFilters}>
              Clear Filters
            </Button>
          )
        }
      />
    ) : (
      <EmptyState
        icon={PackageSearch}
        title="Nothing here just yet"
        description="New pieces are being added to this part of the collection."
      />
    );
  }

  return (
    <div className={PRODUCT_GRID_NARROW_CLASS}>
      {products.map((product, index) => (
        // The first row loads eagerly to protect LCP (§28).
        <ProductCard key={product.id} product={product} priority={index < 4} />
      ))}
    </div>
  );
}


// Re-exported for callers that imported it from here.
export { ProductGridSkeleton };
